import theme from '@assets/theme';
import { useFetchJiraConfig } from '@http/queries/useFetchJiraConfig';
import { useFetchLicenses } from '@http/queries/useFetchLicenses';
import { useFetchTodos } from '@http/queries/useFetchTodos';
import { useFetchWhoami } from '@http/queries/useFetchWhoami';
import { ThemeProvider, CssBaseline } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { fromUnixTime } from 'date-fns';
import { useAtom } from 'jotai';
import { useEffect } from 'react';
import { Outlet, useMatches, ScrollRestoration } from 'react-router-dom';
import { useBootScreen } from '@/hooks/useBootScreen';
import { tokenExpiresAtom, userAtom } from './global-store';
import clearStorageWithExceptions from './helpers/clearStorageWithExceptions';
import {
  clearPostLoginRedirect,
  readPostLoginRedirect,
  redirectToLogin,
  rememberPostLoginRedirect,
  resolvePostLoginTarget,
} from './helpers/postLoginRedirect';
import {
  LOGIN_PATH,
  hasPathPrefix,
  isAuthenticationPath,
} from './helpers/authenticationPaths';
import { resolveRouteAccessRedirect } from '@/guards/routeAccess';
import { MainLayout } from '@layout/MainLayout';
import Header from '@layout/Header/Header';
import { usePathname } from '@/hooks/usePathname';
import PrivacyDialog from './pages/authentication/PrivacyDialog';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation';
import { usePostHog } from '@posthog/react';
import {
  disablePostHogTracking,
  enablePostHogTracking,
  isPostHogTrackingEnabled,
} from '@/posthogClient';
import { Notification } from '@eye-able/ui';
import { useApplyBrandButtonVars } from '@/branding/useOrgBranding';
import { isUnauthenticatedError } from '@/http/authErrors';

function BaseRoute() {
  const posthog = usePostHog();
  const { t, i18n, ready: i18nReady } = useInlineTranslation();

  // Re-colours the shared yellow CTA button to the org brand colour, app-wide.
  useApplyBrandButtonVars();

  const queryClient = useQueryClient();

  const {
    isLoading: LicenseDataLoading,
    isError: LicenseDataError,
    error: licenseDataRequestError,
    isApplied: licenseDataApplied,
  } = useFetchLicenses();
  const {
    isLoading: whoamiLoading,
    isError: whoamiError,
    error: whoamiRequestError,
  } = useFetchWhoami();

  const { isLoading: todosLoading } = useFetchTodos();

  useFetchJiraConfig();

  const [user, setUser] = useAtom(userAtom);
  const [tokenExpires, setTokenExpires] = useAtom(tokenExpiresAtom);
  const matches = useMatches();
  const path = usePathname();
  const { enableBootScreen, disableBootScreen } = useBootScreen();

  const isUserReady =
    !whoamiLoading &&
    !whoamiError &&
    !LicenseDataLoading &&
    licenseDataApplied &&
    !todosLoading &&
    !!user;

  const isBootError = whoamiError || LicenseDataError;
  const isBooting = !isUserReady && !isBootError;

  const isPublicRoute =
    hasPathPrefix(path, '/privacy-policy') ||
    hasPathPrefix(path, '/demo') ||
    isAuthenticationPath(path);
  const showPublicHeader =
    !user && matches.some((match) => match.handle?.publicHeader);
  const isSessionExpired =
    !isPublicRoute &&
    (isUnauthenticatedError(whoamiRequestError) ||
      isUnauthenticatedError(licenseDataRequestError));

  useEffect(() => {
    if (!isSessionExpired) return;

    disablePostHogTracking({ reset: true });
    setUser(undefined);
    clearStorageWithExceptions();
    redirectToLogin();
  }, [isSessionExpired, setUser]);

  // The pending post-login target has served its purpose (or was never needed)
  // once the user is on a real page. Dropping it here keeps a stale deep link
  // from hijacking a later login in the same tab. Skipped while the session is
  // expiring, since the effect above has just stored this very page.
  useEffect(() => {
    if (isSessionExpired || !user || isAuthenticationPath(path)) return;

    clearPostLoginRedirect();
  }, [isSessionExpired, user, path]);

  useEffect(() => {
    if (isUserReady) return;

    disablePostHogTracking();
    Sentry.setTag('orgId', undefined);
  }, [isUserReady]);

  useEffect(() => {
    if (!isUserReady) return;

    enablePostHogTracking();

    Sentry.setTag(
      'orgId',
      user.primaryOrganisation == null
        ? undefined
        : String(user.primaryOrganisation),
    );

    if (isPostHogTrackingEnabled()) {
      posthog.identify(user.id);
      posthog.setPersonProperties({
        organization: user.company,
        organization_id: user.primaryOrganisation,
        role: user.role,
        is_customer: user.isCustomer,
      });
    }
  }, [posthog, isUserReady, user]);

  // The automatic initial pageview is dropped while tracking is disabled.
  // Capture it after authentication; PostHog handles later history changes.
  useEffect(() => {
    if (isUserReady) {
      posthog.capture('$pageview');
    }
  }, [posthog, isUserReady]);

  useEffect(() => {
    if (i18nReady && !isBooting) {
      disableBootScreen();
    } else {
      enableBootScreen();
    }
  }, [
    disableBootScreen,
    isBooting,
    user,
    path,
    i18n.resolvedLanguage,
    queryClient,
    i18nReady,
    enableBootScreen,
  ]);

  useEffect(() => {
    if (!tokenExpires || !user) return;
    // show an alert before the token expires
    // TODO: show a dialog with relogin option
    const now = new Date();

    const expires = Number(tokenExpires);

    const expiresInSeconds = expires - (now.getTime() / 1000).toFixed(0);

    const expiresInMs = expiresInSeconds * 1000;

    const expireDate = fromUnixTime(Number(tokenExpires));

    // check if the token will expires in less than 24 days, which is the max value for setTimeout
    if (expiresInMs < 2147480000) {
      // logout the user when the token expires
      setTimeout(() => {
        setUser(undefined);
        setTokenExpires(undefined);
        alert(
          'Your session has expired, please login again on the following page',
        );
        redirectToLogin();
      }, expiresInMs);

      // show an alert 10 minutes before the token expires
      // TODO: show a dialog with relogin option
      setTimeout(() => {
        console.log('Token will expire in 10 minutes');
        // Set the minutes to 10 minutes before the token expires
        expireDate.setMinutes(expireDate.getMinutes() - 10);

        // Get hours and minutes, adding a leading zero if necessary
        const hours = expireDate.getHours().toString().padStart(2, '0');
        const minutes = expireDate.getMinutes().toString().padStart(2, '0');

        alert(
          `Your Session will expire in 10 minutes (at ${hours}:${minutes}), please logout and login again`,
        );
      }, expiresInMs - 6000);
    }
  }, [tokenExpires, user, setUser, setTokenExpires]);

  // Reload page if localStorage changes from another tab
  useEffect(() => {
    function reloadPage(event) {
      if (!document.hidden) return; // check if the tab is hidden

      const { key, oldValue, newValue } = event;

      if (
        ['reload', 'user', 'X-Dev-Token', 'i18nextLng'].includes(key) &&
        newValue !== oldValue
      ) {
        window.location.reload();
      }
    }

    // this event only fires when the localStorage is changed from another tab
    window.addEventListener('storage', reloadPage);

    return () => {
      window.removeEventListener('storage', reloadPage);
    };
  }, []);

  if (!i18nReady || isBooting || isSessionExpired) {
    return null;
  }

  if (user && !user.hasPassword && path !== '/authentication/change-password') {
    return window.location.assign('/authentication/change-password');
  } else {
    const redirectPath = resolveRouteAccessRedirect(user, matches);

    // Signed out on a protected route: keep the link the visitor opened so that
    // login can return them to it. Stored here rather than in an effect because
    // the navigation below may leave the page before effects run.
    if (redirectPath === LOGIN_PATH) {
      rememberPostLoginRedirect();
    }

    const targetPath = resolvePostLoginTarget({
      redirectPath,
      pathname: path,
      pendingTarget: readPostLoginRedirect(),
    });

    if (targetPath && path !== targetPath) {
      return window.location.assign(targetPath);
    }
  }

  return (
    <ThemeProvider theme={theme} key={i18n.resolvedLanguage}>
      <CssBaseline />

      <ScrollRestoration />

      {!user || isAuthenticationPath(path) ? (
        <>
          {showPublicHeader && <Header isPublic />}
          {showPublicHeader ? (
            // Same grey canvas and content padding as MainLayout's main column.
            <main
              id='main-content'
              className='eye:min-h-svh eye:w-full eye:bg-grey-10 eye:px-16 eye:pt-32 eye:pb-32 eye:lg:px-48 eye:lg:py-[40px]'
            >
              <div className='eye:mx-auto eye:w-full eye:max-w-[1500px]'>
                <Outlet />
              </div>
            </main>
          ) : (
            <Outlet />
          )}
        </>
      ) : (
        <MainLayout />
      )}
      {path.includes('privacy-policy') ? null : <PrivacyDialog />}

      <Notification
        toastOptions={{
          closeButtonAriaLabel: t('close'),
        }}
        aria-live='polite'
      />
    </ThemeProvider>
  );
}

export default BaseRoute;
